import {
  Box,
  FieldWrapper,
  FormField,
  FormGrid,
  Icon,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useCallback, useState } from 'react';

import NumberSelect from 'components/powerFlowShared/components/numberSelect';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AnnouncementPicker } from 'components/shared/announcementPicker';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { FlowCallNodeCallback } from '../nodeTypes';

import styles from './callback.module.scss';

const ANNOUNCEMENT_FIELD = 'announcement';

type Props = FlowManagerEditModalComponentT<FlowCallNodeCallback>;

type Errors = {
  [Key in typeof ANNOUNCEMENT_FIELD]?: string;
};

export function Callback({
  node,
  nodeLabel,
  triggerClose,
  replaceNode,
}: Props): JSX.Element {
  const { payload } = node;
  const {
    from_number: savedFromNumber,
    announcement: savedAnnouncement,
    next_node_id: savedNextNodeId,
  } = payload || {};

  const [fromNumber, setFromNumber] = useState(savedFromNumber ?? '');
  const [announcement, setAnnouncement] = useState(
    savedAnnouncement?.file ?? null,
  );
  const isChanged = useIsChanged(
    [savedFromNumber, fromNumber],
    [savedAnnouncement?.file, announcement],
  );
  const [errors, setErrors] = useState<Errors>({});

  const validate = useCallback(() => {
    const errors: Errors = {};

    if (!announcement) {
      errors[ANNOUNCEMENT_FIELD] = 'Please select an announcement file';
    }

    setErrors(errors);

    return Object.keys(errors).length === 0;
  }, [setErrors, announcement]);
  const handleSave = useCallback(() => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const payload: FlowCallNodeCallback = {
      ...node,
      nodeType: FlowNodeTypes.CALLBACK,
      text: 'Callback',
      icon: 'i-phone-callback',
      isRemovable: true,
      isEditable: true,
      isAction: false,
      overlay: [],
      payload: {
        digit: '#',
        announcement: {
          file: announcement,
          tts: null,
        },
        from_number: fromNumber,
        next_node_id: savedNextNodeId ?? null,
      },
    };

    replaceNode(payload);
    triggerClose();
  }, [
    fromNumber,
    announcement,
    savedNextNodeId,
    node,
    validate,
    triggerClose,
    replaceNode,
  ]);

  const announcementError = errors[ANNOUNCEMENT_FIELD];

  return (
    <Box width="600px">
      <ModalHead
        className={styles.modalHead}
        title={nodeLabel}
        onClose={triggerClose}
      />
      <ModalBody className={styles.modalBody}>
        <Box className={styles.update}>
          <Text.Paragraph className={styles.updateTitle} variant="1">
            <Icon
              className={styles.updateIcon}
              color={colors.purple[500]}
              name="exclamation"
            />{' '}
            Important update
          </Text.Paragraph>
          <Text.Paragraph>
            We’ve made Callbacks a more integrated part of your flow building
            experience. Callbacks can now be activated directly when you’re
            adding Queue actions. Don’t worry, this won’t have an impact on your
            existing callback setup!
          </Text.Paragraph>
        </Box>
        <Text.Paragraph subdued>
          If you don&apos;t want your customers to wait in a queue for too long,
          you can offer them a callback.
        </Text.Paragraph>
        <div className={styles.instructions}>
          <strong>How to set up a callback pattern:</strong>
          <ol>
            <li>
              Enable a timeout on the queue action that comes before this
              callback action. Choose after how many seconds the customer should
              wait in line before they can request a callback by pressing #.
            </li>
            <li>
              Add an endpoint after the callback action (e.g. a queue,
              voicemail, or forward action).
            </li>
          </ol>
        </div>
        <FormGrid className={styles.form}>
          <FormField>
            <AnnouncementPicker
              selectedAudio={announcement}
              onSelect={setAnnouncement}
              adapter={AdapterTypes.Announcement}
              fieldProps={{ meta: { error: announcementError } }}
            />
          </FormField>
          <FormField>
            <FieldWrapper label="Select callback outbound number">
              <NumberSelect
                selectedNumber={fromNumber}
                onSelect={setFromNumber}
              />
            </FieldWrapper>
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFoot
        confirmOnClick={handleSave}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{ disabled: !isChanged }}
        cancelOnClick={triggerClose}
      />
    </Box>
  );
}
